import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Box, TextField, Button, Typography, Paper, Container, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, Chip, Divider, IconButton, Tooltip, Stack, InputAdornment, CircularProgress
} from '@mui/material';
import axios from '../../api/axiosConfig';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';

// --- 미리보기용 비디오 컴포넌트 ---
const VideoPreview = ({ src }) => {
  const videoRef = useRef(null);
  const handleMouseEnter = () => { if (videoRef.current && src) videoRef.current.play().catch(() => {}); };
  const handleMouseLeave = () => { if (videoRef.current && src) { videoRef.current.pause(); videoRef.current.currentTime = 0.5; } };

  return (
    <Box 
      onMouseEnter={handleMouseEnter} 
      onMouseLeave={handleMouseLeave}
      sx={{ 
        width: 100, height: 60, borderRadius: 1, overflow: 'hidden', 
        backgroundColor: '#000', cursor: 'pointer', border: '1px solid #eee'
      }}
    >
      {src ? (
        <video
          ref={videoRef}
          src={src + "#t=0.5"}
          width="100%" height="100%"
          muted playsInline
          controlsList="nodownload" 
          onContextMenu={(e) => e.preventDefault()} 
          style={{ objectFit: 'cover' }}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="caption" color="grey.600">NO VIDEO</Typography>
        </Box>
      )}
    </Box>
  );
};

const VideoManage = () => {
  const API_URL = '/api/video';
  const CODE_URL = '/api/common-code/group'; 
  const THEME_NAVY = '#1a233a';
  
  const [videos, setVideos] = useState([]);
  const [typeCodes, setTypeCodes] = useState([]);   
  const [groupCodes, setGroupCodes] = useState([]); 
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [filterGroup, setFilterGroup] = useState('ALL');
  
  const [formData, setFormData] = useState({
    title: '',
    videoGroup: '',
    videoType: '',
    description: '',
    tags: '',
    useYn: 'Y'
  });

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.get(API_URL, {
        params: {
          search: filterText,
          type: filterType === 'ALL' ? '' : filterType,
          group: filterGroup === 'ALL' ? '' : filterGroup
        }
      });
      setVideos(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error("데이터 로딩 실패:", error);
    } finally {
      setLoading(false);
    }
  }, [filterText, filterType, filterGroup]);

  const fetchCodes = useCallback(async () => {
    try {
      // 그룹이 00001, 유형이 00002로 변경됨에 따라 호출 ID 수정
      const [groupRes, typeRes] = await Promise.all([
        axios.get(`${CODE_URL}/00001`),
        axios.get(`${CODE_URL}/00002`)
      ]);
      setGroupCodes(groupRes.data || []);
      setTypeCodes(typeRes.data || []);
    } catch (error) {}
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) setSelectedFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append("video", new Blob([JSON.stringify(formData)], { type: "application/json" }));
    if (selectedFile) data.append("file", selectedFile);

    try {
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (isEditMode) {
        await axios.put(`${API_URL}/${editId}`, data, config);
        alert('수정되었습니다.');
      } else {
        if (!selectedFile) return alert('영상 파일을 선택해주세요.');
        await axios.post(API_URL, data, config);
        alert('등록되었습니다.');
      }
      resetForm();
      fetchVideos();
    } catch (error) { alert('처리 실패'); }
  };

  const handleEdit = (video) => {
    setIsEditMode(true);
    setEditId(video.videoPk);
    setFormData({
      title: video.title,
      videoGroup: video.videoGroup || '',
      videoType: video.videoType || '',
      description: video.description || '',
      tags: video.tags || '',
      useYn: video.useYn || 'Y'
    });
    setSelectedFile(null);
  };

  const resetForm = () => {
    setIsEditMode(false);
    setEditId(null);
    setSelectedFile(null);
    setFormData({ title: '', videoGroup: '', videoType: '', description: '', tags: '', useYn: 'Y' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <VideoLibraryIcon sx={{ mr: 1, color: THEME_NAVY, fontSize: 30 }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: THEME_NAVY }}>비디오 마스터 관리</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* --- 좌측: 등록/수정 섹션 --- */}
        <Paper elevation={3} sx={{ p: 3, width: { xs: '100%', lg: '380px' }, height: 'fit-content', borderTop: isEditMode ? '4px solid #ed6c02' : `4px solid ${THEME_NAVY}`, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
              {isEditMode ? <EditIcon sx={{ mr: 1 }} color="warning" /> : <AddCircleOutlineIcon sx={{ mr: 1 }} color="primary" />}
              {isEditMode ? '비디오 수정' : '신규 비디오 등록'}
            </Typography>
            {isEditMode && <IconButton size="small" onClick={resetForm}><CloseIcon /></IconButton>}
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="비디오 제목" name="title" value={formData.title} onChange={handleChange} margin="normal" required size="small" />
            
            <Box sx={{ my: 1 }}>
              <Button
                variant="outlined" component="label" fullWidth startIcon={<CloudUploadIcon />}
                color={selectedFile ? "success" : "primary"} sx={{ py: 1, textTransform: 'none' }}
              >
                <Typography variant="body2" noWrap sx={{ maxWidth: '250px' }}>
                  {selectedFile ? selectedFile.name : (isEditMode ? "영상 교체 (선택)" : "영상 파일 업로드")}
                </Typography>
                <input type="file" hidden accept="video/*" onChange={handleFileChange} />
              </Button>
            </Box>

            {/* 위치 변경: 그룹이 왼쪽, 유형이 오른쪽 */}
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>그룹</InputLabel>
                <Select name="videoGroup" value={formData.videoGroup} label="그룹" onChange={handleChange}>
                  {groupCodes.map(c => <MenuItem key={c.detailCode} value={c.detailCode}>{c.detailCodeName}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>유형</InputLabel>
                <Select name="videoType" value={formData.videoType} label="유형" onChange={handleChange}>
                  {typeCodes.map(c => <MenuItem key={c.detailCode} value={c.detailCode}>{c.detailCodeName}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>

            <TextField fullWidth label="태그" name="tags" value={formData.tags} onChange={handleChange} margin="normal" size="small" placeholder="쉼표 구분" />
            <TextField fullWidth label="상세설명" name="description" value={formData.description} onChange={handleChange} margin="normal" multiline rows={2} size="small" />
            
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>사용여부</InputLabel>
              <Select name="useYn" value={formData.useYn} label="사용여부" onChange={handleChange}>
                <MenuItem value="Y">Y (공개함)</MenuItem>
                <MenuItem value="N">N (숨김처리)</MenuItem>
              </Select>
            </FormControl>

            <Button fullWidth variant="contained" type="submit" sx={{ mt: 3, py: 1.2, backgroundColor: isEditMode ? '#ed6c02' : THEME_NAVY, fontWeight: 'bold' }}>
              {isEditMode ? '수정 내용 저장' : '비디오 데이터 저장'}
            </Button>
          </form>
        </Paper>

        {/* --- 우측: 검색 및 목록 섹션 --- */}
        <Box sx={{ flexGrow: 1 }}>
          <Paper elevation={1} sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', backgroundColor: '#fdfdfd', borderRadius: 2 }}>
            <TextField 
              size="small" 
              placeholder="제목 또는 태그 검색" 
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchVideos()}
              sx={{ minWidth: '250px' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
            />
            {/* 위치 변경: 그룹 필터가 먼저, 유형 필터가 다음 */}
            <FormControl size="small" sx={{ minWidth: '120px' }}>
              <InputLabel>그룹 필터</InputLabel>
              <Select label="그룹 필터" value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
                <MenuItem value="ALL">전체 그룹</MenuItem>
                {groupCodes.map(c => <MenuItem key={c.detailCode} value={c.detailCode}>{c.detailCodeName}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: '120px' }}>
              <InputLabel>유형 필터</InputLabel>
              <Select label="유형 필터" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                <MenuItem value="ALL">전체 유형</MenuItem>
                {typeCodes.map(c => <MenuItem key={c.detailCode} value={c.detailCode}>{c.detailCodeName}</MenuItem>)}
              </Select>
            </FormControl>
            
            <Button 
              variant="contained" 
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />} 
              onClick={fetchVideos}
              sx={{ bgcolor: THEME_NAVY }}
            >
              조회
            </Button>
            
            <Button startIcon={<RefreshIcon />} onClick={() => { setFilterText(''); setFilterType('ALL'); setFilterGroup('ALL'); setHasSearched(false); setVideos([]); }}>
              초기화
            </Button>
          </Paper>

          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: '#f4f6f8' }}>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 1.5 }}>미리보기</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>비디오 상세 정보</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>분류</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>상태</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>등록 정보</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>수정 정보</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>액션</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
                ) : !hasSearched ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 15 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} />
                      <Typography variant="body1" color="text.secondary" fontWeight="bold">
                        검색 조건을 설정한 후 조회 버튼을 클릭해주세요.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : videos.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 10 }}>검색 결과가 없습니다.</TableCell></TableRow>
                ) : (
                  videos.map((row) => (
                    <TableRow key={row.videoPk} hover sx={{ backgroundColor: editId === row.videoPk ? '#fff4e5' : 'inherit' }}>
                      <TableCell align="center">
                        <VideoPreview src={row.videoUrl} />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: THEME_NAVY }}>{row.title}</Typography>
                          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', mt: 0.5, fontWeight: 500 }}>
                            <AttachFileIcon sx={{ fontSize: 14, mr: 0.3 }} /> {row.originName || '파일명 정보 없음'}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mt: 0.3 }}>
                            <FingerprintIcon sx={{ fontSize: 14, mr: 0.3 }} /> ID: {row.videoPk}
                          </Typography>
                          {row.tags && (
                              <Box sx={{ mt: 0.5 }}>
                                  {row.tags.split(',').map((tag, idx) => (
                                      <Typography key={idx} variant="caption" sx={{ color: '#ed6c02', mr: 1 }}>#{tag.trim()}</Typography>
                                  ))}
                              </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Stack spacing={0.5} alignItems="center">
                          {/* 칩 순서도 그룹이 상단으로 오도록 조정 가능 (현재는 코드별로 표시) */}
                          <Chip label={groupCodes.find(c => c.detailCode === row.videoGroup)?.detailCodeName || row.videoGroup} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#e8eaed', fontWeight: 'bold' }} />
                          <Chip label={typeCodes.find(c => c.detailCode === row.videoType)?.detailCodeName || row.videoType} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold' }} />
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={row.useYn === 'Y' ? '활성' : '비활성'} size="small" color={row.useYn === 'Y' ? 'success' : 'default'} sx={{ fontWeight: 'bold' }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{row.createUser || '시스템'}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDateTime(row.createDate)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{row.updateUser || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.updateDate ? formatDateTime(row.updateDate) : '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" justifyContent="center">
                          <Tooltip title="수정"><IconButton size="small" color="primary" onClick={() => handleEdit(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="삭제"><IconButton size="small" color="error" onClick={() => { if(window.confirm('이 비디오 데이터를 삭제하시겠습니까?')) axios.delete(`${API_URL}/${row.videoPk}`).then(() => fetchVideos()) }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'right', color: 'text.secondary' }}>
            Total: {videos.length}건
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default VideoManage;